import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import BusinessReportFilter, {
  reportPresets,
} from "../../components/businessReport/BusinessReportFilter";
import BusinessReportKpiCards from "../../components/businessReport/BusinessReportKpiCards";
import BusinessReportTabs from "../../components/businessReport/BusinessReportTabs";
import { pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchBusinessReport,
  getBusinessReport,
  getBusinessReportError,
  getBusinessReportFilter,
  getBusinessReportLoading,
} from "../../features/businessReport/businessReportSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { GET } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BusinessReportFilterProps } from "../../types/businessReport";
import { BreadcrumbType } from "../../types/common";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "બિઝનેસ રિપોર્ટ" },
];

const pad = (value: number) => String(value).padStart(2, "0");

const toYmd = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const presetRange = (preset: string): BusinessReportFilterProps => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  if (preset === "today") {
    return { fromDate: toYmd(today), toDate: toYmd(today) };
  }

  if (preset === "lastMonth") {
    return {
      fromDate: toYmd(new Date(year, month - 1, 1)),
      toDate: toYmd(new Date(year, month, 0)),
    };
  }

  if (preset === "financialYear") {
    return {
      fromDate: toYmd(new Date(month >= 3 ? year : year - 1, 3, 1)),
      toDate: toYmd(today),
    };
  }

  return { fromDate: toYmd(new Date(year, month, 1)), toDate: toYmd(today) };
};

const BusinessReportPage = () => {
  const dispatch = useAppDispatch();

  const report = useAppSelector(getBusinessReport);
  const appliedFilter = useAppSelector(getBusinessReportFilter);
  const isLoading = useAppSelector(getBusinessReportLoading);
  const error = useAppSelector(getBusinessReportError);
  const loggingUser = useAppSelector(getLoggingUser);

  const canExport = hasPermission(
    loggingUser?.permissions,
    permission.businessReportsReport
  );

  const [filter, setFilter] = useState<BusinessReportFilterProps>(
    () => appliedFilter || presetRange("thisMonth")
  );
  const [activeTab, setActiveTab] = useState<string>("summary");
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const activePreset =
    reportPresets.find((preset) => {
      const range = presetRange(preset.key);
      return (
        range.fromDate === filter.fromDate && range.toDate === filter.toDate
      );
    })?.key || null;

  const loadReport = (next: BusinessReportFilterProps) => {
    dispatch(fetchBusinessReport(next)).then((action: any) => {
      if (action.payload?.data && action.payload.data.status !== "success") {
        toast.error(action.payload.data.message, {
          ...(toastStyles as any),
        });
      }
    });
  };

  const isValidRange = (next: BusinessReportFilterProps) => {
    if (!next.fromDate || !next.toDate) {
      toast.error("કૃપા કરીને તારીખ દાખલ કરો.", {
        ...(toastStyles as any),
      });
      return false;
    }

    if (next.fromDate > next.toDate) {
      toast.error("તારીખ થી, તારીખ સુધી કરતાં મોટી ન હોઈ શકે.", {
        ...(toastStyles as any),
      });
      return false;
    }

    return true;
  };

  useEffect(() => {
    loadReport(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (value: Partial<BusinessReportFilterProps>) => {
    setFilter((previous) => ({ ...previous, ...value }));
  };

  const handlePreset = (preset: string) => {
    const next = presetRange(preset);
    setFilter(next);
    loadReport(next);
  };

  const handleApply = () => {
    if (isValidRange(filter)) {
      loadReport(filter);
    }
  };

  const handleReset = () => {
    const next = presetRange("thisMonth");
    setFilter(next);
    loadReport(next);
  };

  const handleExport = async () => {
    if (!isValidRange(filter)) {
      return;
    }

    try {
      setIsExporting(true);
      const response: any = await GET(`/v1/admin/business-reports/pdf`, {
        fromDate: filter.fromDate,
        toDate: filter.toDate,
      });

      if (response.data.status === "success") {
        dispatch(updatePdfFile(response.data.data));
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>બિઝનેસ રિપોર્ટ</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <BusinessReportFilter
          filter={filter}
          activePreset={activePreset}
          canExport={canExport}
          isLoading={isLoading}
          isExporting={isExporting}
          onChange={handleChange}
          onPreset={handlePreset}
          onApply={handleApply}
          onReset={handleReset}
          onExport={handleExport}
        />

        <BusinessReportKpiCards
          kpis={report?.kpis || null}
          isLoading={isLoading}
        />

        <BusinessReportTabs
          tabs={report?.tabs || []}
          activeTab={activeTab}
          appliedFilter={appliedFilter}
          isLoading={isLoading}
          error={error}
          onTabChange={setActiveTab}
        />
      </div>
    </AppLayout>
  );
};
export default BusinessReportPage;
